import { slugify } from '@/utils/slug';

/**
 * The vocabulary the roster is described by, deliberately closed and
 * deliberately coarse.
 *
 * A logo wall's job is not to list every name, it is to show that the
 * buyers already here span the sectors a new buyer belongs to. A free-text
 * sector would drift into one slightly different phrasing per client and the
 * pattern would stop being readable at a glance; a union makes the compiler
 * refuse a new one.
 *
 * Granularity is set by one rule: **no sector may hold a single member**. A
 * group of one on /clients reads as a mistake rather than a category, which is
 * why insurance sits with banking, aviation with logistics, and mining,
 * agriculture and manufacturing together under resources. That rule is also
 * what to apply when a new client arrives — either it joins a group, or it
 * arrives with the second member that justifies a new one.
 *
 * Short wherever short is honest: these are set as a tag under a logo and as a
 * filter chip on /clients, and a phrase that wraps to two lines stops being
 * either.
 */
export type ClientSector =
  | 'Government'
  | 'Banking & Insurance'
  | 'Lending & Asset Finance'
  | 'Energy & Utilities'
  | 'Education'
  | 'Healthcare'
  | 'Logistics & Aviation'
  | 'Tourism & Travel'
  | 'Hospitality'
  | 'Construction & Engineering'
  | 'Industry & Resources'
  | 'Technology & Telecoms'
  | 'Media & Events'
  | 'Professional Services'
  | 'Associations & Non-profit';

export type Client = {
  /** Shown as the logo's alt text, and as the tile's caption in every case. */
  name: string;
  /**
   * What the organisation does. Shown as a tag under the name, and as the
   * grouping and the filter on /clients — a logo a visitor does not recognise
   * is an ornament until something says who it belongs to.
   */
  sector: ClientSector;
  /** Matched against logo file names in `src/assets/clients/`. */
  slug: string;
  /**
   * Other names the artwork may arrive under. Only needed where the file is
   * unlikely to be named after the full title — an initialism, usually.
   */
  aliases?: readonly string[];
  /**
   * The only logo key to accept, matched exactly.
   *
   * An escape hatch from the fuzzy matching in `config/clientLogos.ts`, which
   * accepts a file name one whole word longer or shorter than the slug so that
   * `roko.png` finds Roko Construction. Two cases need it:
   *
   * - Two clients share a first word, and so the fuzzy match is not wrong but
   *   ambiguous: `rwenzori.png` is the water brand, and without this Rwenzori
   *   Rare Metals would wear its logo. Set it wherever a name is a prefix of
   *   another client's and let the fuzzy match do the rest.
   * - The file is not named after the company at all — a download keeping the
   *   stock-library's name (`…-mubs-seeklogo.png`), a mangled or misspelt
   *   export (`gloTranssssss.jpg`, `mulago-hospital-ogo.jpg`), or a name taken
   *   from the mark rather than the owner. Renaming it is the obvious move and
   *   the wrong one: artwork is re-supplied over the years, the next copy
   *   arrives under the sender's name again, and the rename has to be
   *   remembered. Pinning the key leaves the file as it came.
   *
   * The value is the key `logoKey()` derives, not the file name: lower-cased,
   * hyphenated, with a leading or trailing `logo` dropped. `Steadfast Creative
   * Laurel Anchor Logo.png` is `steadfast-creative-laurel-anchor`.
   */
  logoKey?: string;
  /**
   * Ignore the artwork on disk and render the monogram instead.
   *
   * For a logo file that exists but cannot be used — in practice a white or
   * near-white mark drawn for a dark background. The client tile is white in
   * both colour schemes (`ClientLogoTile` explains why it has to be), so such
   * a mark composites to a blank card. A tile showing nothing is worse than a
   * tile showing initials — the first reads as a broken image, the second as a
   * logo not yet supplied.
   *
   * No client needs it at present; it is kept because the case recurs every
   * time a dark-background export arrives.
   *
   * It is deliberately a flag rather than a deleted file. The artwork is the
   * client's own and someone sent it to us; it stays in `src/assets/clients/`
   * so it can be re-cropped or re-coloured rather than asked for again. Clear
   * the flag when a dark-ink version replaces it.
   *
   * Check a new logo with the ink probe before trusting it — a mark that is
   * invisible here looked fine to whoever exported it, against their own
   * background.
   */
  logoPending?: boolean;
  /**
   * On the home page's marquee. Everyone else appears on /clients.
   *
   * Eighteen of the roster, chosen for recognition rather than for size of
   * order: the band's job is to be read in a glance by a Ugandan buyer, and a
   * wall of the whole roster drifting past is one nobody can read at all (see
   * `ClientsMarquee`).
   */
  featured?: boolean;
  /**
   * The initials shown until artwork arrives, where the derived ones would be
   * wrong or unrecognisable. See `clientMonogram`.
   */
  monogram?: string;
};

type ClientInput = {
  name: string;
  aliases?: readonly string[];
  logoKey?: string;
  logoPending?: boolean;
  featured?: boolean;
  monogram?: string;
};

/** A plain string is the common case: a name with nothing else to declare. */
type RosterMember = string | ClientInput;

type RosterGroup = { sector: ClientSector; members: readonly RosterMember[] };

/**
 * The organisations Kabex Motors has supplied vehicles to (IA §4).
 *
 * Declared grouped by sector rather than as a flat list, which is the one
 * change that makes a roster this size maintainable: the sector is written
 * once per group instead of once per client, a new client is added next to its
 * peers, and the shape of the file is the shape of the /clients page. Order
 * within a group runs from the most widely recognised name downwards.
 *
 * Nothing here is ordered for the marquee. The two rows used to be dealt
 * straight off this list, which meant the declaration order was quietly
 * load-bearing; `useClientLogoRows` now balances the rows itself, by artwork
 * rather than by position, so a logo arriving cannot unbalance them.
 *
 * A name here needs no logo file to render: `ClientMark` falls back to a
 * monogram and the tile captions the name, so the roster is never blocked on
 * an asset.
 *
 * Names are as Kabex supplied them, with four corrections: "JUCIARY" →
 * "Judiciary", "SEWAREGE" → "Sewerage", "DEV'T" → "Development", and the
 * curly apostrophe normalised. Initialisms keep their capitals; everything
 * else is Title Case, because the tiles are not set in caps.
 */
const roster: readonly RosterGroup[] = [
  {
    sector: 'Government',
    members: [
      { name: 'Ministry of Finance', aliases: ['MoFPED'], monogram: 'MoF', featured: true },
      { name: 'Ministry of Health', aliases: ['MoH'], monogram: 'MoH', featured: true },
      { name: 'Ministry of Internal Affairs', aliases: ['MIA'], monogram: 'MIA' },
      {
        name: 'Ministry of Lands, Housing & Urban Development',
        aliases: ['Ministry Lands'],
        monogram: 'MLHUD',
      },
      // "THE JUCIARY" as supplied. The existing artwork is `judiciary-ug.png`,
      // which the alias finds.
      { name: 'The Judiciary of Uganda', aliases: ['Judiciary'], monogram: 'JoU' },
      { name: 'Uganda Revenue Authority', aliases: ['URA'], monogram: 'URA', featured: true },
      { name: 'Parliament of Uganda', aliases: ['Parliament'], monogram: 'PoU', featured: true },
      { name: 'KCCA', aliases: ['Kampala Capital City Authority'], featured: true },
      { name: 'UNBS', aliases: ['UNBSUG', 'Uganda National Bureau of Standards'] },
    ],
  },
  {
    sector: 'Banking & Insurance',
    members: [
      {
        name: 'Stanbic Bank Uganda',
        aliases: ['Stanbic Bank', 'Stanbic'],
        monogram: 'SBU',
        featured: true,
      },
      { name: 'ABSA', featured: true },
      { name: 'Centenary Bank', featured: true },
      { name: 'Bank of Africa', monogram: 'BOA' },
      'Pearl Bank',
      { name: 'DFCU Bank', aliases: ['DFCU'], monogram: 'DFCU', featured: true },
      { name: 'DTB Bank', aliases: ['DTB', 'Diamond Trust Bank'], monogram: 'DTB' },
      { name: 'MUA Insurance', monogram: 'MUA' },
    ],
  },
  {
    // Finance houses rather than banks, and the distinction is the reader's
    // rather than ours: someone scanning this page for a peer is asking "has a
    // business like mine bought here", and a lender to SMEs has more in common
    // with the next finance house than with Stanbic. Glo Trans lends to SMEs
    // and individuals; Cynatek Holdings was supplied without an expansion, and
    // its sector is our reading of the mark rather than anything we were told.
    // Neither belongs under a chip that reads "Banking".
    sector: 'Lending & Asset Finance',
    members: [
      { name: 'Glo Trans Financial Services', logoKey: 'glotranssssss', monogram: 'GTF' },
      // The file is `cynatek-holdings.png`, one whole word shorter than the
      // slug, which is exactly what the fuzzy match in `config/clientLogos.ts`
      // is for — no `logoKey` needed while nothing else starts with "cynatek".
      { name: 'Cynatek Holdings Limited', monogram: 'CYN' },
    ],
  },
  {
    sector: 'Energy & Utilities',
    members: [
      { name: 'UNOC', aliases: ['Uganda National Oil Company'], featured: true },
      { name: 'EACOP', featured: true },
      {
        name: 'National Water & Sewerage Corporation',
        aliases: ['NWSC'],
        monogram: 'NWSC',
        // Two files arrived for this one. `nwsc_png.png` is a white mark on
        // transparency and composites to a blank card, so the key names the
        // other, which is the full-colour crest.
        logoKey: 'national-water',
        featured: true,
      },
      'UEDCL',
      { name: 'Stabex Uganda', monogram: 'SU' },
    ],
  },
  {
    sector: 'Education',
    members: [
      // `logoKey` because the MUBS file name begins with this one's slug: without
      // it the fuzzy match hands Makerere the business school's crest.
      { name: 'Makerere University', logoKey: 'makerere-univ', monogram: 'MAK', featured: true },
      // The file arrived as a stock-library export, which no rule can guess.
      { name: 'MUBS', logoKey: 'makerere-university-business-school-mubs-seeklogo' },
      { name: 'Cavendish University', monogram: 'CU' },
      { name: 'Kabale University', monogram: 'KU' },
      { name: 'Boston International School', monogram: 'BIS' },
    ],
  },
  {
    sector: 'Healthcare',
    members: [
      // The file is `mulago-hospital-ogo.jpg` — a typo for "logo" in the name we
      // were sent, so the key has to carry it.
      {
        name: 'Mulago Hospital Research Department',
        logoKey: 'mulago-hospital-ogo',
        monogram: 'MUL',
      },
      { name: 'Nakasero Hospital', aliases: ['NHL'], featured: true },
      { name: 'Platinum Hospital', monogram: 'PH' },
      { name: 'Neocare Clinic & Fertility Center', aliases: ['Neo Care'], monogram: 'NEO' },
    ],
  },
  {
    sector: 'Logistics & Aviation',
    members: [
      { name: 'Uganda Airlines', monogram: 'UA', featured: true },
      { name: 'DHL', featured: true },
      // Supplied as "SIFCO (SIF CARGO)" — both names are in use, so the tile
      // carries the one the alias cannot.
      { name: 'SIFCO (SIF Cargo)', aliases: ['SIFCO', 'SIF Cargo'], monogram: 'SIFCO' },
      { name: 'Ship With Us Cargo', monogram: 'SWU' },
    ],
  },
  {
    sector: 'Tourism & Travel',
    members: [
      { name: 'Kabiza Wilderness Safari Ltd', monogram: 'KWS' },
      // `logoKey` because the file arrived as `berengeifricasafaris.gif` — the
      // web handle with a letter eaten, which matches no form of the name.
      { name: 'Berengei Tours Africa Ltd', logoKey: 'berengeifricasafaris', monogram: 'BTA' },
      { name: 'Hudma Safari Ltd', monogram: 'HS' },
      { name: 'Bingi Safaris Limited', aliases: ['Bingi Safari'], monogram: 'BS' },
    ],
  },
  {
    sector: 'Hospitality',
    members: [{ name: 'Ridar Hotel', monogram: 'RH' }, 'BMK Group'],
  },
  {
    sector: 'Construction & Engineering',
    members: [
      'Roko Construction',
      // Supplied as the initialism alone, with no expansion. Left as given
      // rather than guessed at; the sector is our reading of it (see below).
      {
        name: 'Keane Engineering Technologies Ltd',
        aliases: ['Kean Engineering'],
        monogram: 'KET',
      },
      {
        name: 'Emmanuel & Damen Technical Services',
        aliases: ['Emmanuel and Damen'],
        monogram: 'EDT',
      },
    ],
  },
  {
    sector: 'Industry & Resources',
    members: [
      'Lato Milk',
      { name: 'Rwenzori', aliases: ['Rwenzori Water'], logoKey: 'rwenzori' },
      // `logoKey` because "Rwenzori" is a prefix of this name: without it the
      // fuzzy match would hand this tile the water brand's logo.
      { name: 'Rwenzori Rare Metals Limited', logoKey: 'rwenzori-rare-metals', monogram: 'RRM' },
      { name: 'Rustic Roots Ranch Ltd', monogram: 'RRR' },
    ],
  },
  {
    sector: 'Technology & Telecoms',
    members: [
      { name: 'MTN Uganda', aliases: ['MTN'], monogram: 'MTN', featured: true },
      {
        name: 'Uganda Telecommunications Corporation Limited',
        aliases: ['UTCL'],
        monogram: 'UTCL',
      },
      { name: 'Barter Kings U Ltd', monogram: 'BK' },
    ],
  },
  {
    sector: 'Media & Events',
    members: [{ name: 'Vision Group', featured: true }, 'Silk Events'],
  },
  {
    // Firms that sell expertise to other businesses, which is what the two of
    // them have in common and what a reader scanning for a peer is looking
    // for. The group exists because the alternative was worse: this was
    // "Trade & Business" holding JK Auditors by itself, and a sector of one is
    // the one shape the roster's grouping rule forbids — on the page it reads
    // as a filing error rather than as a category.
    //
    // Steadfast Creative joins it from "Media & Events". An agency sells the
    // same thing an audit firm does, and Media & Events keeps Vision Group and
    // Silk Events, which are a broadcaster and an events house — the two that
    // made the name in the first place.
    sector: 'Professional Services',
    members: [
      { name: 'JK Auditors & Tax Consultants', monogram: 'JK' },
      // `logoKey` because the artwork is named for the mark it draws — the
      // laurel and anchor — rather than for the company.
      {
        name: 'Steadfast Creative Limited',
        logoKey: 'steadfast-creative-laurel-anchor',
        monogram: 'SC',
      },
    ],
  },
  {
    sector: 'Associations & Non-profit',
    members: ['KACITA', { name: 'Good Forest Foundation', monogram: 'GFF' }],
  },
];

/**
 * Words that carry no recognition and so are skipped when initials are taken.
 *
 * Company suffixes are in here as well as grammatical filler: "Berengei Tours
 * Africa Ltd" wants B-T-A, not B-T-A-L, and a monogram ending in "L" for
 * Limited is a monogram that says nothing.
 */
const MONOGRAM_SKIP = new Set([
  'of',
  'and',
  'the',
  'for',
  'ltd',
  'limited',
  'plc',
  'co',
  'company',
  'group',
  'u',
  'uganda',
]);

/** At three letters a monogram still reads as a mark; at four it reads as a word. */
const MONOGRAM_MAX = 3;

/** An initialism as supplied: capitals only, long enough to be a mark. */
const isInitialism = (word: string) => /^[A-Z0-9]{2,6}$/.test(word);

/**
 * The initials a logo-less tile shows above the name.
 *
 * Four rules, in order, because no single one of them is right for all
 * the roster's names:
 *
 * 1. A `monogram` on the roster entry wins. Most entries set one, because "MF"
 *    for the Ministry of Finance is technically correct and useless to a reader.
 * 2. An initialism inside the name is taken whole. MTN, KACITA, UEDCL, ABSA,
 *    BMK and SIFCO are what these organisations are actually *called*, and
 *    reducing "BMK Group" to "BG" would invent a mark nobody uses.
 * 3. Otherwise, the initials of up to three recognition-carrying words — the
 *    suffixes and filler in `MONOGRAM_SKIP` dropped, so "Berengei Tours Africa
 *    Ltd" gives BTA rather than BTAL.
 * 4. Where that leaves fewer than two letters, the skipping is undone and then,
 *    failing that, the name's own first two letters are used. "Rwenzori" has
 *    only one word to give and "R" is not a monogram.
 *
 * Exported for the tile and for tests; nothing else should need it.
 */
export function clientMonogram(client: Pick<Client, 'name' | 'monogram'>): string {
  if (client.monogram) return client.monogram;

  const words = client.name.split(/[\s,]+/).filter(Boolean);
  const embedded = words.find(isInitialism);
  if (embedded) return embedded;

  const significant = words.filter((word) => /^[a-z0-9]/i.test(word));
  const initials = (list: readonly string[]) =>
    list
      .slice(0, MONOGRAM_MAX)
      .map((word) => word[0]!.toUpperCase())
      .join('');

  const trimmed = initials(significant.filter((w) => !MONOGRAM_SKIP.has(w.toLowerCase())));
  if (trimmed.length >= 2) return trimmed;

  const untrimmed = initials(significant);
  if (untrimmed.length >= 2) return untrimmed;

  // A one-word name: its own opening, cased as a mark rather than as a word.
  return `${client.name.slice(0, 1).toUpperCase()}${client.name.slice(1, 2).toLowerCase()}`;
}

const toClient = (member: RosterMember, sector: ClientSector): Client => {
  const input = typeof member === 'string' ? { name: member } : member;
  return {
    name: input.name,
    sector,
    slug: slugify(input.name),
    ...(input.aliases ? { aliases: input.aliases } : {}),
    ...(input.logoKey ? { logoKey: input.logoKey } : {}),
    ...(input.logoPending ? { logoPending: input.logoPending } : {}),
    ...(input.featured ? { featured: input.featured } : {}),
    ...(input.monogram ? { monogram: input.monogram } : {}),
  };
};

/** One sector and its members, in roster order — the shape /clients draws. */
export type ClientSectorGroup = {
  sector: ClientSector;
  slug: string;
  clients: readonly Client[];
};

export const clientSectorGroups: readonly ClientSectorGroup[] = roster.map((group) => ({
  sector: group.sector,
  slug: slugify(group.sector),
  clients: group.members.map((member) => toClient(member, group.sector)),
}));

/** Every client, grouped order flattened. The roster, for counts and for AT. */
export const clients: readonly Client[] = clientSectorGroups.flatMap((group) => group.clients);

/** The eighteen the home page's marquee carries. See `Client.featured`. */
export const featuredClients: readonly Client[] = clients.filter((client) => client.featured);
